import {
  EllipsisOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Dropdown,
  Form,
  Modal,
  Popover,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  message,
  Tooltip,
} from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  PAYMENT_INTENT_ERROR_STATUS_TO_TEXT,
  PAYMENT_INTENT_STATUS,
} from "common/constants";
import {
  camelToWords,
  formatCurrency,
  formatDate,
  snakeToCamelCase,
  toTitleCase,
} from "common/utils";
import { TruncatedText } from "components/truncatedText";
import {
  useBulkDebtorDeletePaymentIntentsMutation,
  useClearOverpaymentsMutation,
  useGetPaymentIntentsQuery,
  useRefundPaymentIntentMutation,
  useUnsettlePaymentIntentMutation,
} from "features/payments/paymentsAPI";
import GeneratePaymentReceiptModal from "features/payments/components/generatePaymentReceiptModal";
import { useFetchDebtorProfileQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { PaymentAllocationsTable } from "features/scheduledPayments/paymentAllocationsTable";
import { TransactionsTable } from "features/scheduledPayments/transactionsTable";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import moment from "moment-timezone";

const StyledEllipsisOutlined = styled(EllipsisOutlined)`
  font-size: 24px;
`;

const StyledErrorText = styled.div`
  color: red;
`;

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const { confirm } = Modal;

function ExpandedRow({ paymentIntentId }) {
  return (
    <>
      <PaymentAllocationsTable paymentIntentId={paymentIntentId} />
      <TransactionsTable paymentIntentId={paymentIntentId} />
    </>
  );
}

// Stable reference for Table's expandedRowRender to avoid ESLint react/no-unstable-nested-components
const renderExpandedRow = (record) => <ExpandedRow paymentIntentId={record.id} />;

export default function PaymentHistory() {
  const [form] = Form.useForm();
  const { debtorId } = useParams();
  const navigate = useNavigate();
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const { data: paymentIntents, isLoading } = useGetPaymentIntentsQuery({
    debtorId,
    isIncludePacketedPayments: true,
  });
  const [deletePaymentIntent] = useBulkDebtorDeletePaymentIntentsMutation();
  const [refundPaymentIntent] = useRefundPaymentIntentMutation();
  const [clearOverpayments] = useClearOverpaymentsMutation();
  const [unsettlePaymentIntent] = useUnsettlePaymentIntentMutation();
  const { data: debtorPersonalInfo } = useFetchDebtorProfileQuery({ debtorId });
  const { data: constants, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const {
    data: {
      [PERMISSIONS.PAYMENT__DELETE]: isPaymentDeleteAuthorized,
      [PERMISSIONS.PAYMENT__REVERSE]: isPaymentReversalAuthorized,
      [PERMISSIONS.PAYMENT__CLEAR_OVERPAYMENTS]: isClearOverpaymentsAuthorized,
      [PERMISSIONS.PAYMENT__GET]: isPaymentGetAuthorized,
    },
  } = useAuthorizations();

  const items = [
    {
      key: "reverse",
      label: "Reverse Payment",
      actionHandler: refundPaymentIntent,
      isVisible: (record) =>
        isPaymentReversalAuthorized && record.status === PAYMENT_INTENT_STATUS.EXECUTED,
    },
    {
      key: "markAsReversed",
      label: "Mark as Reversed (Aktos Only)",
      actionHandler: (payload) => {
        return refundPaymentIntent({ ...payload, aktosOnly: true });
      },
      isVisible: (record) =>
        isPaymentReversalAuthorized &&
        record.status === PAYMENT_INTENT_STATUS.EXECUTED &&
        !record.isLoggingOnly,
    },
    {
      key: "delete",
      label: "Delete Payment",
      actionHandler: deletePaymentIntent,
      isVisible: (record) =>
        // Always available for logging only payments.
        // For processed payments, refund/void attempt is required before the delete option appears.
        isPaymentDeleteAuthorized &&
        (record.isLoggingOnly ||
          [
            PAYMENT_INTENT_STATUS.REFUNDED,
            PAYMENT_INTENT_STATUS.VOIDED,
            PAYMENT_INTENT_STATUS.EXECUTE_ERROR,
            PAYMENT_INTENT_STATUS.REFUND_ERROR,
            PAYMENT_INTENT_STATUS.VOID_ERROR,
          ].includes(record.status)),
    },
    {
      key: "unsettle",
      label: "Undo Settlement",
      actionHandler: unsettlePaymentIntent,
      isVisible: (record) =>
        record.status === PAYMENT_INTENT_STATUS.EXECUTED && record.isSettlement,
    },
    {
      key: "clearOverpayments",
      label: "Clear Overpayments (Aktos Only)",
      actionHandler: (payload) => {
        return clearOverpayments({ ...payload });
      },
      isVisible: (record) =>
        isClearOverpaymentsAuthorized && parseFloat(record.currentOverpayment) > 0,
    },
    {
      key: "generateReceipt",
      label: "Generate Receipt",
      isVisible: (record) =>
        isPaymentGetAuthorized && record.status === PAYMENT_INTENT_STATUS.EXECUTED,
    },
  ];

  const handleOk = (item, key, record) => {
    const isReversal = key === "reverse" || key === "markAsReversed";

    const keyTitles = {
      markAsReversed:
        "Are you sure you want to mark this payment as reversed (Aktos only)? Please ensure the reversal has already been processed by the external payment processor.",
      clearOverpayments:
        "Are you sure you want to clear overpayments for this payment (Aktos only)? Please ensure the overpayment has already been refunded by the external payment processor.",
    };

    confirm({
      title: keyTitles[key] || `Are you sure you want to ${key} this payment?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          {key === "delete" && (
            <p>
              Deleting this payment will not affect the last "interest calculation date" for the
              affected accounts.
            </p>
          )}
          {isReversal && (
            <Form form={form}>
              <Form.Item name="reversalReason" label="Reason">
                <Select
                  defaultValue="other"
                  options={constants?.paymentReversalReasons?.map((reason) => ({
                    label: reason.display,
                    value: reason.value,
                  }))}
                />
              </Form.Item>
            </Form>
          )}
        </div>
      ),
      async onOk() {
        if (key === "delete" && record.debtorId.toString() !== debtorId.toString()) {
          message.error(
            `Payment cannot be deleted because ${debtorPersonalInfo?.name} is not the main debtor of this payment`,
          );
          return;
        }

        const values = await form.getFieldsValue();
        const result = await item.actionHandler({
          debtorId,
          paymentIntentId: record.id,
          paymentIntentIds: [record.id],
          ...values,
        });

        if ("error" in result) {
          switch (key) {
            case "markAsReversed":
              message.error("Failed to mark payment as reversed");
              break;
            default:
              message.error(`Failed to ${key} payment`);
          }
        }
        if ("data" in result) {
          switch (key) {
            case "delete":
              message.success("Payment successfully deleted");
              break;
            case "reverse":
              message.success("Payment successfully reversed");
              break;
            case "markAsReversed":
              message.success("Payment successfully marked as reversed");
              break;
            case "unsettle":
              message.success("Payment successfully unsettled");
              break;
            default:
              message.success(`Payment successfully ${key}ed`);
          }
          setExpandedKeys([record.id]);
        }
      },
    });
  };

  const handleMenuClick = ({ item, key, keyPath, domEvent }, record) => {
    // This prevents navigation to invoice detail when clicking on a dropdown item
    domEvent.preventDefault();
    domEvent.stopPropagation();

    if (key === "generateReceipt") {
      setPaymentRecord(record);
      return;
    }

    // Call the action handler defined in the items array
    handleOk(item.props, key, record);
  };

  const columns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      key: "id",
      // NOTE: On the PaymentHistoryTab, we sort by ID, since these payments have already been processed.
      sorter: (a, b) => parseInt(a.id, 10) - parseInt(b.id, 10),
      render: (text, record) =>
        record.isSettlement ? (
          <>
            <Tag color="green">SETTLEMENT</Tag>
            {text}
          </>
        ) : (
          text
        ),
    },
    {
      title: "Paid By",
      dataIndex: "debtor",
      key: "debtor",
      render: (text, record) => {
        return (
          <div key={record.debtorId}>
            <Button
              type="link"
              onClick={() => {
                navigate(`/debtors/${record.debtorId}`);
              }}
            >
              {record.debtor?.name} (ID: {record.debtor?.externalId})
            </Button>
          </div>
        );
      },
    },
    {
      title: "Account IDs",
      dataIndex: "externalAccountIds",
      key: "externalAccountIds",
      render: (text, record) => record.externalAccountIds?.join(", ")?.toUpperCase() ?? "-",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAtRawDate) => (createdAtRawDate ? formatDate(createdAtRawDate) : "-"),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      render: (text, record) =>
        record.scheduledDate && moment(record.scheduledDate).format(DATE_FORMAT),
      sorter: (a, b) => moment(a.scheduledDate).unix() - moment(b.scheduledDate).unix(),
    },
    {
      title: "Last Transaction",
      dataIndex: "lastTransaction",
      key: "lastTransaction",
      render: (_, record) =>
        record.lastTransaction
          ? moment(record.lastTransaction.createdAt).format(DATE_TIME_FORMAT)
          : "-",
    },
    {
      title: "Last Successful Transaction",
      dataIndex: "lastSuccessfulExecute",
      key: "lastSuccessfulExecute",
      render: (_, record) =>
        record.lastSuccessfulExecute
          ? moment(record.lastSuccessfulExecute.createdAt).format(DATE_TIME_FORMAT)
          : "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => `$${record.totalAmount}`,
    },
    {
      title: "Convenience Fee",
      dataIndex: "convenienceFeeAmount",
      render: (text) => (text ? formatCurrency(text) : "-"),
    },
    {
      title: "Overpayments",
      dataIndex: "currentOverpayment",
      key: "currentOverpayment",
      render: (text, record) =>
        text ? (
          <Space>
            {formatCurrency(text)}
            {parseFloat(record.overpaymentAmountCleared) > 0 && (
              <Tooltip
                title={`Original Amount: ${formatCurrency(record.overpayment)}`}
                trigger="hover"
              >
                <InfoCircleOutlined />
              </Tooltip>
            )}
          </Space>
        ) : (
          "-"
        ),
    },
    {
      title: "Reference #",
      dataIndex: "referenceNumber",
      key: "referenceNumber",
      render: (text) => text || "-",
    },
    {
      title: "Attribution",
      dataIndex: "attributionSource",
      key: "attributionSource",
      width: 100,
      render: (text) => (text ? camelToWords(snakeToCamelCase(text)) : "-"),
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (text, record) => {
        if (
          text === PAYMENT_INTENT_STATUS.VOID_ERROR ||
          text === PAYMENT_INTENT_STATUS.REFUND_ERROR ||
          text === PAYMENT_INTENT_STATUS.EXECUTE_ERROR
        ) {
          const errorText = PAYMENT_INTENT_ERROR_STATUS_TO_TEXT[text] || "Error";
          return (
            <Row align="middle">
              <StyledErrorText>{errorText}</StyledErrorText>
              <Popover
                content="Failed to communicate with payment processor. Please log into your payment portal, cancel any existing transaction, and delete this payment to start over."
                trigger="hover"
                placement="right"
              >
                <StyledQuestionCircleOutlined />
              </Popover>
            </Row>
          );
        }
        if (record.isLastTransactionDeclined) {
          return <StyledErrorText>declined</StyledErrorText>;
        }
        return text;
      },
    },
    {
      title: "Payment Method",
      key: "paymentMethod",
      render: (text, record) => {
        const { type, cardLast4Digits, bankAccountLast4Digits } = record.paymentMethod ?? {};
        if (type === "process_card") {
          return `Card ending in ${cardLast4Digits}`;
        }
        if (type === "process_ach") {
          return `ACH ending in ${bankAccountLast4Digits}`;
        }
        if (type === "paid_to_creditor_legal") {
          return `Paid to Creditor (Legal Payment)`;
        }
        if (type === "forwarding_entity_legal") {
          return `Paid to Forwarding Entity (Legal Payment)`;
        }
        return toTitleCase({ str: type, delimiter: "_" });
      },
    },
    {
      title: "Data Source",
      key: "dataSource",
      render: (text, record) =>
        constants?.editableDataSourceTypes?.find((x) => x.value === record.dataSource)?.display ??
        "-",
    },
    {
      title: "Reversal Reason",
      key: "reversalReason",
      render: (text, record) =>
        constants?.paymentReversalReasons?.find((x) => x.value === record.reversalReason)
          ?.display ?? "-",
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 400,
      render: (notes) => <TruncatedText text={notes || "-"} maxLength={40} />,
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "action",
      align: "center",
      key: "action",
      render: (_, record) => {
        if (record.debtorId !== parseInt(debtorId, 10)) {
          return <div>No actions available</div>;
        }
        const itemsToDisplay = items.filter((each) => each.isVisible(record));
        if (itemsToDisplay.length === 0) {
          return <div>No actions available</div>;
        }
        return (
          <Space size="middle">
            <Dropdown
              menu={{
                items: itemsToDisplay,
                onClick: (clickHandlerProp) => handleMenuClick(clickHandlerProp, record),
              }}
              trigger={["click"]}
            >
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                type="text"
              >
                <StyledEllipsisOutlined />
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  return isConstantsLoading ? (
    <Skeleton />
  ) : (
    <>
      <Table
        scroll={{ x: "max-content" }}
        loading={isLoading}
        bordered
        rowClassName={() => "editable-row"}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 30, 100],
        }}
        // @ts-ignore
        columns={columns}
        dataSource={paymentIntents?.filter(
          (paymentIntent) =>
            paymentIntent.isLastTransactionDeclined ||
            [
              PAYMENT_INTENT_STATUS.EXECUTED,
              PAYMENT_INTENT_STATUS.REFUNDED,
              PAYMENT_INTENT_STATUS.VOIDED,
              PAYMENT_INTENT_STATUS.EXECUTE_ERROR,
              PAYMENT_INTENT_STATUS.REFUND_ERROR,
              PAYMENT_INTENT_STATUS.VOID_ERROR,
            ].includes(paymentIntent.status),
        )}
        expandable={{
          expandedRowRender: renderExpandedRow,
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: (keys) => {
            setExpandedKeys((prevKeys) => {
              // This logic takes care of closing old expanded rows
              return keys.filter((x) => !prevKeys.includes(x));
            });
          },
        }}
      />
      {paymentRecord && (
        <GeneratePaymentReceiptModal
          open
          paymentIntentId={paymentRecord?.id}
          onCancel={() => setPaymentRecord(null)}
          onOk={() => setPaymentRecord(null)}
        />
      )}
    </>
  );
}
